import React, { useState } from 'react';
import { Clock, MapPin, User, Calendar, Camera, Building2, Search, Eye } from 'lucide-react';
import { 
  pointages as pointagesData, 
  users, 
  centres,
  getUserById,
  getCentreById,
  getTechniciens
} from '../../data/mockData';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function ConsultationPointages({ user }) {
  const techniciens = getTechniciens();
  const [filterTechnicien, setFilterTechnicien] = useState('');
  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');
  const [selectedPointage, setSelectedPointage] = useState(null);

  // Vérifier que l'utilisateur est admin
  if (user.role !== 'admin') {
    return (
      <div className="page-container">
        <div className="alert alert-error">
          Vous n'avez pas accès à cette page.
        </div>
      </div>
    );
  }

  const filteredPointages = pointagesData.filter(p => {
    const matchTechnicien = !filterTechnicien || p.technicienId === parseInt(filterTechnicien);
    
    let matchDateDebut = true;
    let matchDateFin = true;
    
    if (filterDateDebut) {
      matchDateDebut = p.date >= filterDateDebut;
    }
    if (filterDateFin) {
      matchDateFin = p.date <= filterDateFin;
    }
    
    return matchTechnicien && matchDateDebut && matchDateFin;
  }).sort((a, b) => {
    // Trier par date décroissante puis par heure
    if (a.date !== b.date) {
      return b.date.localeCompare(a.date);
    }
    return b.heureDebut?.localeCompare(a.heureDebut) || 0;
  });

  const calculerDuree = (pointage) => {
    if (!pointage.heureDebut || !pointage.heureFin) return '-';
    
    const [hd, md] = pointage.heureDebut.split(':').map(Number);
    const [hf, mf] = pointage.heureFin.split(':').map(Number);
    const totalMinutes = (hf * 60 + mf) - (hd * 60 + md);
    
    const heures = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${heures}h${minutes.toString().padStart(2, '0')}`;
  };

  const handleViewDetails = (pointage) => {
    setSelectedPointage(pointage);
  };

  const closeModal = () => {
    setSelectedPointage(null);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Consultation des pointages</h1>
          <p className="text-muted">Historique des pointages des techniciens</p>
        </div>
      </div>

      <div className="filters-bar">
        <div className="form-group">
          <label htmlFor="filterTechnicien">Technicien</label>
          <select
            id="filterTechnicien"
            value={filterTechnicien}
            onChange={(e) => setFilterTechnicien(e.target.value)}
            className="filter-select"
          >
            <option value="">Tous les techniciens</option>
            {techniciens.map(tech => (
              <option key={tech.id} value={tech.id}>
                {tech.prenom} {tech.nom}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="filterDateDebut">Du</label>
          <input
            type="date"
            id="filterDateDebut"
            value={filterDateDebut}
            onChange={(e) => setFilterDateDebut(e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="filterDateFin">Au</label>
          <input
            type="date"
            id="filterDateFin"
            value={filterDateFin}
            onChange={(e) => setFilterDateFin(e.target.value)}
            className="filter-input"
          />
        </div>

        <button 
          onClick={() => {
            setFilterTechnicien('');
            setFilterDateDebut('');
            setFilterDateFin('');
          }}
          className="btn btn-secondary"
        >
          Réinitialiser
        </button>
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Technicien</th>
              <th>Centre</th>
              <th>Début</th>
              <th>Fin</th>
              <th>Durée</th>
              <th>GPS</th>
              <th>Photo</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPointages.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center text-muted">
                  Aucun pointage trouvé
                </td>
              </tr>
            ) : (
              filteredPointages.map(pointage => {
                const technicien = getUserById(pointage.technicienId);
                const centre = getCentreById(pointage.centreId);
                
                return (
                  <tr key={pointage.id}>
                    <td>
                      <strong>
                        {format(new Date(pointage.date), 'EEEE d MMMM', { locale: fr })}
                      </strong>
                    </td>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar user-avatar-sm">
                          {technicien?.prenom[0]}{technicien?.nom[0]}
                        </div>
                        <span>{technicien?.prenom} {technicien?.nom}</span>
                      </div>
                    </td>
                    <td>{centre?.nom}</td>
                    <td>
                      <span className="badge badge-primary">{pointage.heureDebut}</span>
                    </td>
                    <td>
                      {pointage.heureFin ? (
                        <span className="badge badge-success">{pointage.heureFin}</span>
                      ) : (
                        <span className="badge badge-warning">En cours</span>
                      )}
                    </td>
                    <td>
                      <strong>{calculerDuree(pointage)}</strong>
                    </td>
                    <td>
                      {pointage.coordonneesGPSDebut ? (
                        <span className="badge badge-success">
                          <MapPin size={12} /> OK
                        </span>
                      ) : (
                        <span className="badge badge-secondary">Non</span>
                      )}
                    </td>
                    <td>
                      {pointage.photoFin ? (
                        <span className="badge badge-success">
                          <Camera size={12} /> Oui
                        </span>
                      ) : (
                        <span className="badge badge-secondary">-</span>
                      )}
                    </td>
                    <td>
                      <button 
                        onClick={() => handleViewDetails(pointage)}
                        className="btn btn-sm btn-secondary"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de détails */}
      {selectedPointage && (
        <>
          <div className="modal-overlay" onClick={closeModal}></div>
          <div className="modal">
            <div className="modal-header">
              <h2>Détails du pointage</h2>
              <button onClick={closeModal} className="modal-close">×</button>
            </div>
            
            <div className="modal-body">
              {(() => {
                const technicien = getUserById(selectedPointage.technicienId);
                const centre = getCentreById(selectedPointage.centreId);
                
                return (
                  <>
                    <div className="modal-section">
                      <h3>Informations générales</h3>
                      <div className="info-grid">
                        <div className="info-item">
                          <p className="text-muted">Date</p>
                          <p><strong>{format(new Date(selectedPointage.date), 'EEEE d MMMM yyyy', { locale: fr })}</strong></p>
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Technicien</p>
                          <p>{technicien?.prenom} {technicien?.nom}</p>
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Centre</p>
                          <p>{centre?.nom}</p>
                        </div>
                        <div className="info-item">
                          <p className="text-muted">Durée totale</p>
                          <p className="text-primary"><strong>{calculerDuree(selectedPointage)}</strong></p>
                        </div>
                      </div>
                    </div>

                    <div className="modal-section">
                      <h3>Horaires</h3>
                      <div className="pointage-horaires">
                        <div className="pointage-horaire-item">
                          <div className="pointage-horaire-icon start">
                            <Clock size={24} />
                          </div>
                          <div>
                            <p className="text-muted">Début de journée</p>
                            <p className="pointage-horaire-time">{selectedPointage.heureDebut}</p>
                            {selectedPointage.coordonneesGPSDebut && (
                              <p className="text-sm text-muted">
                                <MapPin size={12} className="inline" />
                                {selectedPointage.coordonneesGPSDebut.latitude.toFixed(4)}, 
                                {selectedPointage.coordonneesGPSDebut.longitude.toFixed(4)}
                              </p>
                            )}
                            {!selectedPointage.coordonneesGPSDebut && (
                              <p className="text-sm text-warning">
                                <MapPin size={12} className="inline" /> GPS non disponible
                              </p>
                            )}
                          </div>
                        </div>

                        {selectedPointage.heureFin ? (
                          <div className="pointage-horaire-item">
                            <div className="pointage-horaire-icon end">
                              <Clock size={24} />
                            </div>
                            <div>
                              <p className="text-muted">Fin de journée</p>
                              <p className="pointage-horaire-time">{selectedPointage.heureFin}</p>
                              {selectedPointage.coordonneesGPSFin && (
                                <p className="text-sm text-muted">
                                  <MapPin size={12} className="inline" />
                                  {selectedPointage.coordonneesGPSFin.latitude.toFixed(4)}, 
                                  {selectedPointage.coordonneesGPSFin.longitude.toFixed(4)}
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="pointage-horaire-item en-cours">
                            <div className="pointage-horaire-icon warning">
                              <Clock size={24} />
                            </div>
                            <div>
                              <p className="text-muted">Fin de journée</p>
                              <p className="text-warning"><strong>En cours...</strong></p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedPointage.photoFin && (
                      <div className="modal-section">
                        <h3>Photo de l'atelier (fin de journée)</h3>
                        <div className="photo-modal">
                          <img 
                            src={selectedPointage.photoFin} 
                            alt="Photo atelier fin de journée" 
                            className="modal-image"
                          />
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            
            <div className="modal-footer">
              <button onClick={closeModal} className="btn btn-secondary">
                Fermer
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ConsultationPointages;

